import { ChevronDown, ChevronLeft, EllipsisVertical, PenLine, SquareUserRound, Trash2 } from 'lucide-react';
import { Link, useParams } from 'react-router';
import { useEffect, useRef } from 'react';
import { DataTable } from '../../../components/data-table';
import LayoutAdmin from '../../../components/layouts/LayoutAdmin';
import { columnInvoice, columns } from './Column';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '../../../components/ui/breadcrumb';
import usePartner from '../../../stores/usePartner';
import Skeleton from '../../../components/ui/skeleton';
import useIsMobile from '../../../hooks/use-mobile';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../../components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../../../components/ui/dropdown-menu';
import { Button } from '../../../components/ui/button';
import { useDialog } from '../../../hooks/useDialog';
import EditFormPartner from '../Edit/EditFormPartner';
import { IPartner } from '../../../types/types';
import { useNavigateBack } from '../../../hooks/useNavigateBack';

function ActionMenu() {
  const { partner, loading, deletePartner, getPartner } = usePartner();
  const isMobile = useIsMobile();
  const deleteDialog = useDialog();
  const editDialog = useDialog();
  const closeRef = useRef<HTMLButtonElement>(null);
  const { partnerId } = useParams();

  const handleCloseDialog = () => {
    getPartner(partnerId as string);
    closeRef.current?.click();
  };

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          {!loading && (
            <Button type="button" variant={isMobile ? 'outline' : 'default'}>
              {!isMobile ? (
                <>
                  Action
                  <ChevronDown />
                </>
              ) : (
                <EllipsisVertical />
              )}
            </Button>
          )}
        </DropdownMenuTrigger>
        <DropdownMenuContent className="w-56">
          <DropdownMenuGroup>
            <DropdownMenuItem onClick={editDialog.trigger}>
              <PenLine />
              <span>Edit</span>
            </DropdownMenuItem>
            <DropdownMenuItem onClick={deleteDialog.trigger}>
              <Trash2 />
              <span>Delete</span>
            </DropdownMenuItem>
          </DropdownMenuGroup>
        </DropdownMenuContent>
      </DropdownMenu>
      <Dialog {...deleteDialog.props}>
        <DialogContent className="w-[calc(100dvw-32px)] md:w-full rounded-lg">
          <DialogHeader>
            <DialogTitle>Confirm delete partner</DialogTitle>
            <DialogDescription className="py-4">
              are you sure you want to delete partner <span className="font-semibold">{partner?.partner_id}</span>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex flex-row justify-end gap-2">
            <div>
              <Button variant="default" type="button" onClick={() => deletePartner(partner?.partner_id as string)}>
                Delete
              </Button>
            </div>
            <DialogClose>
              <Button variant="outline">Cancel</Button>
            </DialogClose>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog {...editDialog.props}>
        <DialogContent className="w-[calc(100dvw-32px)] md:w-full rounded-lg">
          <EditFormPartner partner={partner as IPartner} onSuccess={handleCloseDialog} />
          <DialogClose ref={closeRef} className="hidden" />
        </DialogContent>
      </Dialog>
    </>
  );
}

export default function ViewPartnerPage() {
  const { loading, partner, getPartner } = usePartner();
  const { partnerId } = useParams();

  const navigateBack = useNavigateBack('/partner');

  useEffect(() => {
    getPartner(partnerId as string);
  }, [getPartner, partnerId]);

  return (
    <LayoutAdmin header="Partner">
      <div className="flex gap-4 items-center">
        <button
          type="button"
          className="flex items-center gap-1 text-xs md:text-base cursor-pointer font-medium text-secondary"
          onClick={navigateBack}
        >
          <ChevronLeft size={16} />
          Back
        </button>
        <div className="w-2 h-2 rounded-full bg-slate-100" />
        <Breadcrumb>
          <BreadcrumbList>
            <BreadcrumbItem>
              <BreadcrumbLink>
                <Link to="/partner" className="text-xs md:text-base">
                  All Partner
                </Link>
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbPage className="text-xs md:text-base">View Partner</BreadcrumbPage>
            </BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>
      </div>
      <div className="px-2 my-1 flex items-center justify-between">
        <div className="flex flex-wrap gap-2 items-center">
          <SquareUserRound className="w-4 h-4 md:w-6 md:h-6" />
          {loading ? (
            <Skeleton className="w-40 h-6 rounded-md" />
          ) : (
            <p className="md:text-lg text-sm lg:text-xl font-medium">{partner?.name || '-'}</p>
          )}
        </div>
        <ActionMenu />
      </div>
      <div className="w-full">
        <div className="flex flex-col md:flex-row gap-4 items-start">
          <div className="p-4 w-full flex flex-col gap-4 flex-1 border border-border rounded-lg">
            {loading ? (
              <>
                <Skeleton className="w-full h-6 rounded-md mt-2" />
                <Skeleton className="w-full h-6 rounded-md mt-2" />
                <Skeleton className="w-full h-6 rounded-md mt-2" />
                <Skeleton className="w-full h-6 rounded-md mt-2" />
              </>
            ) : (
              <>
                <div className="flex justify-between p-2 bg-slate-200 rounded-md">
                  <p className="flex-1 font-medium text-sm md:text-base">Partner No.</p>
                  <p className="flex-1 font-medium text-sm md:text-base">{partner?.partner_number || '-'}</p>
                </div>
                <div className="flex justify-between p-2">
                  <p className="flex-1 font-medium text-sm md:text-base">Company Type</p>
                  <p className="flex-1 font-medium text-sm md:text-base">{partner?.company_type || '-'}</p>
                </div>
                <div className="flex justify-between p-2 bg-slate-200 rounded-md">
                  <p className="flex-1 font-medium text-sm md:text-base">Email</p>
                  <p className="flex-1 font-medium text-sm md:text-base">{partner?.email || '-'}</p>
                </div>
                <div className="flex justify-between p-2">
                  <p className="flex-1 font-medium text-sm md:text-base">Phone Number</p>
                  <p className="flex-1 font-medium text-sm md:text-base">{partner?.phone || '-'}</p>
                </div>
              </>
            )}
          </div>
          <div className="p-2 flex flex-col gap-2 flex-1 border border-border rounded-lg">
            <p className="font-semibold text-sm md:text-base lg:text-xl ml-1 my-1">Contact Information</p>
            <div className="w-[calc(100dvw-52px)] md:w-full">
              <DataTable columns={columns} data={partner?.contact_persons || []} />
            </div>
          </div>
        </div>
        <div className="mt-4 flex flex-col gap-2 w-[calc(100dvw-32px)] md:w-full">
          <p className="font-semibold lg:text-lg text-sm md:text-base">Invoice</p>
          <DataTable columns={columnInvoice} data={partner?.invoices || []} />
        </div>
      </div>
    </LayoutAdmin>
  );
}
